import { Family, Medium, FeedEntryType } from "protogen/advisors_service_pb";
import { Box, Button, IconButton } from "@mui/material";
import { FeedFocusState } from "../../types/feed";
import { Mail, Phone, Search, Filter } from "lucide-react";
import SearchDialog from "../search/SearchDialog";
import { useState, useContext } from "react";
import useIsMobile from "components/hooks/useIsMobile";
import CheckedDropDown from "components/common/CheckedDropDown";
import { ActivityFeedFilterContext } from "../context/ActivityFeedFilterContext";
import { CurrentUserContext } from "components/context/RequireAuth";
import { ROLE_ADMIN, hasRole } from "common/userUtils";

const FilterDropdown = () => {
  const { entryType, updateEntryType } = useContext(ActivityFeedFilterContext);
  const currentUser = useContext(CurrentUserContext);
  const isAdmin = hasRole(currentUser, ROLE_ADMIN);
  const isMobile = useIsMobile();
  return (
    <CheckedDropDown
      radio={true}
      disabled={false}
      displayValue={
        <IconButton
          sx={{
            border: "1px solid #D4D4D4",
            borderRadius: isMobile ? "50px" : "12px",
            padding: "8px !important",
            flexGrow: "1",
            color: "#6B6E7B",
            backgroundColor: "#fff",
            width: "40px",
          }}
          size="small"
        >
          <Filter height="20px" width="20px" />
        </IconButton>
      }
      hasIcon={false}
      sxSelect={{
        fieldset: {
          border: "none !important",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          padding: "0px",
        },
        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
          border: "none !important",
        },
        ".MuiSelect-select.MuiSelect-outlined.MuiSelect-multiple": {
          padding: "0px",
          margin: "0px",
        },
        minHeight: "40px",
        padding: "0px",
      }}
      options={[
        {
          label: "All",
          value: FeedEntryType.UNSPECIFIED.toString(),
          defaultChecked: entryType === FeedEntryType.UNSPECIFIED,
          mutuallyExclusive: true,
        },
        {
          label: "Calls",
          value: FeedEntryType.PHONE_CALL.toString(),
          defaultChecked: entryType === FeedEntryType.PHONE_CALL,
          mutuallyExclusive: true,
        },
        {
          label: "Task activity",
          value: FeedEntryType.TASK_ACTIVITY.toString(),
          defaultChecked: entryType === FeedEntryType.TASK_ACTIVITY,
          mutuallyExclusive: true,
        },
        {
          label: "Messages",
          value: FeedEntryType.MESSAGE.toString(),
          defaultChecked: entryType === FeedEntryType.MESSAGE,
          mutuallyExclusive: true,
        },
        ...(isAdmin
          ? [
              {
                label: "Admin - All",
                value: FeedEntryType.ADMIN_ALL.toString(),
                defaultChecked: entryType === FeedEntryType.ADMIN_ALL,
                mutuallyExclusive: true,
              },
            ]
          : []),
      ]}
      onChange={(r) => {
        if (r.length === 0) {
          updateEntryType(FeedEntryType.UNSPECIFIED);
          return;
        }
        const value = parseInt(r[0].value);
        updateEntryType(value as FeedEntryType);
      }}
    />
  );
};

type ContactSectionProps = {
  family: Family;
  feedFocusState: FeedFocusState;
  setFeedFocusState: (s: FeedFocusState) => void;
};
export default ({ feedFocusState, setFeedFocusState }: ContactSectionProps) => {
  const isMobile = useIsMobile();
  const [searchOpen, setSearchOpen] = useState(false);
  return (
    <Box>
      {/* Mobile buttons */}
      {isMobile && (
        <Box sx={{ display: "flex", gap: "8px" }}>
          <IconButton
            sx={{
              border: "1px solid #D3D3D3",
              borderRadius: "50px",
              height: "40px",
              width: "40px",
              padding: "8px",
            }}
            onClick={() => {
              setFeedFocusState({
                ...feedFocusState,
                selectedMedium: Medium.PHONE,
              });
            }}
          >
            <Phone />
          </IconButton>
          <IconButton
            sx={{
              border: "1px solid #D3D3D3",
              borderRadius: "50px",
              height: "40px",
              width: "40px",
              padding: "8px",
            }}
            onClick={() => {
              setFeedFocusState({
                ...feedFocusState,
                selectedMedium: Medium.EMAIL,
              });
            }}
          >
            <Mail />
          </IconButton>
          <FilterDropdown />
          <IconButton
            sx={{
              border: "1px solid #D3D3D3",
              borderRadius: "50px",
              height: "40px",
              width: "40px",
              padding: "8px",
            }}
            size="small"
            onClick={() => setSearchOpen(true)}
          >
            <Search height="20px" width="20px" />
          </IconButton>
          <SearchDialog
            open={searchOpen}
            onClose={() => setSearchOpen(false)}
          />
        </Box>
      )}

      {/* Desktop buttons */}
      {!isMobile && (
        <Box
          sx={{
            display: "flex",
            padding: "16px 16px",
            gap: "8px",
          }}
        >
          <Button
            sx={{
              border: "1px solid #D4D4D4",
              borderRadius: "12px",
              padding: "8px 24px !important",
              flexGrow: "1",
              color: "#6B6E7B",
              backgroundColor: "#fff",
              fontSize: "14px",
            }}
            startIcon={<Phone height="20px" width="20px" />}
            variant="text"
            size="small"
            onClick={() => {
              setFeedFocusState({
                ...feedFocusState,
                selectedMedium: Medium.PHONE,
              });
            }}
          >
            Call
          </Button>

          <Button
            sx={{
              border: "1px solid #D4D4D4",
              borderRadius: "12px",
              padding: "8px 24px !important",
              flexGrow: "1",
              color: "#6B6E7B",
              backgroundColor: "#fff",
              fontSize: "14px",
            }}
            startIcon={<Mail height="20px" width="20px" />}
            variant="text"
            size="small"
            onClick={() => {
              setFeedFocusState({
                ...feedFocusState,
                selectedMedium: Medium.EMAIL,
              });
            }}
          >
            Email
          </Button>
          <FilterDropdown />
          <IconButton
            sx={{
              border: "1px solid #D4D4D4",
              borderRadius: "12px",
              padding: "8px !important",
              flexGrow: "0",
              color: "#6B6E7B",
              backgroundColor: "#fff",
              width: "40px",
            }}
            size="small"
            onClick={() => setSearchOpen(true)}
          >
            <Search height="20px" width="20px" />
          </IconButton>
          <SearchDialog
            open={searchOpen}
            onClose={() => setSearchOpen(false)}
          />
        </Box>
      )}
    </Box>
  );
};
